import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCatalogText } from '@/lib/localizeCatalog';
import type { Category } from '@/types';
import { categoryColor, onCategoryColor } from './categoryColor';

interface Props {
  categories: Category[];
  activeId: string | null;
  onPick: (id: string) => void;
  /** SC-730 : nombre de plats par catégorie, affiché sur les tuiles du
   *  panneau. `MenuPage` ne transmet que les catégories qui rendent une
   *  section, donc ce compte n'est jamais nul. */
  counts?: Record<string, number>;
  /** SC-730 bis : le repérage par couleur est réservé au carnet du serveur
   *  (voir `MenuPage`). Sans lui la barre reprend le vert du thème sur la
   *  catégorie active, comme avant SC-730. Le défaut est « sans couleur » :
   *  c'est le client, et c'est la surface la plus large. */
  colored?: boolean;
  /** SC-1278 : carnet du serveur — noms français, comme les titres de `MenuPage`. */
  frenchOnly?: boolean;
}

/** Au-delà de ce nombre de catégories, parcourir la grille devient plus lent
 *  que taper trois lettres : on ajoute alors un champ de recherche. */
const SEARCH_THRESHOLD = 8;

/** Comparaison insensible à la casse ET aux accents : un serveur pressé tape
 *  « entree », pas « Entrée ». */
function fold(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export default function CategoryPills({
  categories,
  activeId,
  onPick,
  counts,
  colored = false,
  frenchOnly = false,
}: Props) {
  const { t } = useTranslation();
  const ct = useCatalogText();
  const nameOf = (c: Category) => (frenchOnly ? c.name : ct.name(c));
  const ref = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const activeCategory = categories.find((c) => c.id === activeId) ?? categories[0];

  const results = useMemo(() => {
    const q = fold(query.trim());
    return q ? categories.filter((c) => fold(c.name).includes(q) || fold(nameOf(c)).includes(q)) : categories;
  }, [categories, query, ct.lang, frenchOnly]);

  useEffect(() => {
    if (!activeId || !ref.current) return;
    const target = ref.current.querySelector<HTMLButtonElement>(`[data-cat-id="${activeId}"]`);
    if (!target) return;
    const container = ref.current;
    const targetLeft = target.offsetLeft;
    const targetRight = target.offsetLeft + target.offsetWidth;
    const visibleLeft = container.scrollLeft;
    const visibleRight = container.scrollLeft + container.clientWidth;
    if (targetRight > visibleRight - 24) {
      container.scrollTo({ left: targetRight - container.clientWidth + 24, behavior: 'smooth' });
    } else if (targetLeft < visibleLeft + 24) {
      container.scrollTo({ left: targetLeft - 24, behavior: 'smooth' });
    }
  }, [activeId]);

  // SC-838 : la barre est collante sous l'en-tete, donc sa hauteur fait partie
  // de la marge de saut des sections. On la publie mesuree — comme
  // `SiteHeader` publie `--site-header-h` — plutot que de la coder en dur :
  // elle vaut 0 des `lg` (la barre cede la place a la colonne laterale) et le
  // parcours sur place n'a pas le meme en-tete que le parcours a emporter.
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => root.style.setProperty('--category-bar-h', `${el.offsetHeight}px`);
    update();
    if (typeof ResizeObserver === 'undefined') {
      return () => root.style.removeProperty('--category-bar-h');
    }
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--category-bar-h');
    };
  }, []);

  // A percentage of the screen alone can extend below its bottom when the
  // sticky header is tall. Use the panel's actual top and the visible viewport
  // so browser chrome, the search keyboard and scrolling cannot hide the end.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel) return;
    const viewport = window.visualViewport;
    const update = () => {
      const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      const available = Math.max(0, bottom - panel.getBoundingClientRect().top);
      panel.style.setProperty('--category-panel-available-h', `${available}px`);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, { passive: true });
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (navRef.current) observer?.observe(navRef.current);
    observer?.observe(document.body);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update);
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      observer?.disconnect();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (categories.length === 0) return null;

  const closePanel = () => {
    setOpen(false);
    setQuery('');
  };

  const pick = (id: string) => {
    closePanel();
    onPick(id);
  };

  return (
    <nav
      ref={navRef}
      className="lg:hidden sticky z-[400] bg-cream border-y-2 border-ink transition-[top] duration-200 motion-reduce:transition-none"
      style={{ top: 'var(--site-header-h, 110px)' }}
      aria-label={t('menu.categories')}
    >
      {/* z-[2] : la barre reste au-dessus du voile du panneau, sinon elle
          s'assombrit avec le reste de la page alors qu'elle porte le bouton
          qui referme. */}
      <div className="relative z-[2] flex items-center gap-2 bg-cream px-2 py-1.5">
        {/* Accès à TOUTES les catégories d'un coup. Un <select> natif était
            plus compact mais iOS le rend en roue de sélection et ignore la
            couleur des <option> : le repère visuel disparaissait exactement
            sur l'appareil du serveur. D'où un panneau maison, en tuiles —
            qui sert aussi le client, en noir et blanc et avec la recherche. */}
        <button
          type="button"
          onClick={() => (open ? closePanel() : setOpen(true))}
          aria-expanded={open}
          aria-controls="category-picker-panel"
          aria-label={t('menu.allCategories')}
          className="shrink-0 flex h-11 items-center gap-1.5 rounded-btn-sm border-2 border-ink bg-ink px-2.5 text-paper shadow-[2px_2px_0_var(--ink)]"
        >
          <svg viewBox="0 0 24 24" className="w-[17px] h-[17px]" fill="currentColor" aria-hidden="true">
            <rect x="3" y="3" width="7.5" height="7.5" rx="1.6" />
            <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6" />
            <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6" />
            <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6" />
          </svg>
          <span aria-hidden="true" className="font-display text-[13px] leading-none">
            {open ? '▴' : '▾'}
          </span>
        </button>

        <div
          ref={ref}
          className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap flex gap-2"
          style={{
            scrollSnapType: 'x proximity',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}
        >
          {categories.map((c) => {
            const active = c.id === activeId;
            const color = colored ? categoryColor(c) : null;
            return (
              <button
                key={c.id}
                type="button"
                data-cat-id={c.id}
                onClick={() => onPick(c.id)}
                className={`shrink-0 min-h-[44px] flex items-center gap-2 px-3 border-2 border-ink rounded-btn font-display text-sm ${
                  active ? 'font-bold shadow-[2px_2px_0_var(--ink)]' : 'bg-paper text-ink'
                } ${active && !color ? 'bg-mint text-[var(--on-primary)]' : ''}`}
                style={{
                  scrollSnapAlign: 'start',
                  ...(active && color
                    ? { backgroundColor: color, color: onCategoryColor(color) }
                    : {}),
                }}
              >
                {/* Pastille pleine quand la catégorie est active, sinon un
                    carré de couleur : la couleur reste lisible dans les deux
                    cas, et « active » ne se confond pas avec « colorée ». */}
                {!active && color && (
                  <span
                    aria-hidden="true"
                    className="block w-3 h-3 rounded-[3px] border border-ink/30 shrink-0"
                    style={{ backgroundColor: color }}
                  />
                )}
                <span>
                  {c.emoji} {nameOf(c)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {open && (
        <>
          {/* Le voile qui detache le panneau de la carte. `bg-black/45` et non
              `bg-ink/45` : mesure au navigateur (2026-08-25), `bg-ink/40`
              sortait `rgba(0, 0, 0, 0)` — voile 100 % transparent, et le
              panneau se confondait avec les fiches produits qu'il recouvre.
              Les couleurs du theme sont des `var(--x)` nues dans
              tailwind.config, sans `<alpha-value>` : un modificateur
              d'opacite dessus n'est pas fiable. `bg-ink/75` (les modales),
              lui, rend bien — donc ne pas en deduire une regle generale :
              toute reecriture avec un jeton du theme se re-mesure. */}
          <div
            className="fixed inset-0 z-[1] bg-black/45 backdrop-blur-[2px]"
            onClick={closePanel}
            aria-hidden="true"
          />
          <div
            ref={panelRef}
            id="category-picker-panel"
            className="absolute inset-x-0 top-full z-[2] overflow-y-auto overscroll-contain border-b-2 border-ink bg-cream px-2 pt-2 shadow-[0_10px_20px_-8px_rgba(0,0,0,0.45)]"
            style={{
              maxHeight: 'min(70dvh, var(--category-panel-available-h, 70dvh))',
              paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))',
            }}
          >
            {/* Pas d'autofocus : sur un téléphone, ouvrir le clavier mangerait
                la moitié des tuiles que le serveur vient d'ouvrir. */}
            {categories.length > SEARCH_THRESHOLD && (
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('menu.searchCategory')}
                aria-label={t('menu.searchCategory')}
                className="mb-2 h-11 w-full rounded-btn-sm border-2 border-ink bg-paper px-3 font-mono text-sm text-ink outline-none"
              />
            )}

            {results.length === 0 ? (
              <p className="px-1 py-6 text-center font-mono text-sm text-ink/60">
                {t('menu.noCategoryMatch')}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {results.map((c) => {
                  const color = colored ? categoryColor(c) : null;
                  const active = c.id === activeCategory?.id;
                  const count = counts?.[c.id];
                  return (
                    <button
                      key={c.id}
                      type="button"
                      data-panel-cat-id={c.id}
                      onClick={() => pick(c.id)}
                      aria-current={active ? 'true' : undefined}
                      className={`flex min-h-[68px] items-start justify-between gap-2 rounded-btn border-2 border-ink px-3 py-2.5 text-start ${
                        active
                          ? 'shadow-[3px_3px_0_var(--ink)] ring-2 ring-ink ring-offset-2 ring-offset-cream'
                          : 'shadow-[2px_2px_0_var(--ink)]'
                      } ${
                        color ? '' : active ? 'bg-mint text-[var(--on-primary)]' : 'bg-paper text-ink'
                      }`}
                      style={color ? { backgroundColor: color, color: onCategoryColor(color) } : undefined}
                    >
                      <span className="font-display text-[15px] leading-tight line-clamp-3">
                        {c.emoji} {nameOf(c)}
                      </span>
                      {count !== undefined && (
                        <span className="shrink-0 font-mono text-[11px] font-bold">{count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </nav>
  );
}
